import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AdminUserApiService } from '@cnt-admin/data-access';
import { ReservationApiService } from '@cnt-workspace/data-access';
import { IReservation, IUser } from '@curbnturf/entities';
import { combineLatest, concat, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { AdminStatusChipComponent } from '../status/admin-status-chip.component';
import { ILookup, parseLookup } from './lookup';
import { UpcomingBooking, upcomingBookings } from './upcoming';

interface IOption {
  key: string;
  link: (string | number)[];
  queryParams?: Record<string, string>;
  reservation?: IReservation;
  user?: IUser;
  /** One of the matched person's upcoming bookings, listed under them. */
  upcoming?: UpcomingBooking;
  /** "See all users matching …" */
  allUsers?: string;
}

/**
 * The admin header's search box: a reservation code (`r-123`) or number opens
 * the reservation; a name, email, phone or id opens that customer. Press "/"
 * anywhere to jump to it. Results float in a CDK overlay because the Ionic
 * toolbar clips anything inside it.
 */
@Component({
  selector: 'admin-global-search',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, DatePipe, AdminStatusChipComponent],
  templateUrl: './admin-global-search.component.html',
  styleUrl: './admin-global-search.component.scss',
})
export class AdminGlobalSearchComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly users = inject(AdminUserApiService);
  private readonly reservations = inject(ReservationApiService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;
  @ViewChild('origin', { static: true, read: ElementRef }) box!: ElementRef<HTMLElement>;

  /** Under the box, lined up with its left edge, or its right edge when there's no room to the right. */
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 6 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 6 },
  ];
  readonly terms$ = new Subject<string>();

  text = '';
  open = false;
  searching = false;
  lookup: ILookup = parseLookup('');
  options: IOption[] = [];
  active = 0;
  panelWidth = 0;
  /** The term the results are for; the box can be ahead of it while typing. */
  private searchedTerm = '';

  ngOnInit(): void {
    this.terms$
      .pipe(
        map((t) => t.trim()),
        debounceTime(250),
        distinctUntilChanged(),
        tap((term) => (this.searchedTerm = term)),
        map(parseLookup),
        tap((lookup) => {
          this.lookup = lookup;
          this.searching = !!(lookup.reservationId || lookup.userTerm);
        }),
        switchMap((lookup) =>
          combineLatest([this.findReservation(lookup), this.findUsers(lookup)]).pipe(
            // Show the matches at once; when exactly one person matched, add their upcoming bookings after.
            switchMap(([reservation, users]) =>
              concat(
                of({ reservation, users, upcoming: [] as UpcomingBooking[], first: true }),
                users.length === 1
                  ? this.findUpcoming(users[0]).pipe(
                      map((upcoming) => ({ reservation, users, upcoming, first: false })),
                    )
                  : EMPTY,
              ),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ reservation, users, upcoming, first }) => {
        this.searching = false;
        this.options = this.toOptions(reservation, users, upcoming);
        if (first || this.active >= this.options.length) this.active = 0;
      });
  }

  /** "/" focuses the box from anywhere that isn't already a text field. */
  @HostListener('document:keydown', ['$event'])
  onDocumentKey(event: KeyboardEvent): void {
    if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"], .ProseMirror')) return;
    event.preventDefault();
    this.input.nativeElement.focus();
  }

  onInput(value: string): void {
    this.text = value;
    // Wider than the box, so names and emails fit, but never wider than the screen.
    this.panelWidth = Math.min(Math.max(this.box.nativeElement.offsetWidth, 420), window.innerWidth - 24);
    this.open = value.trim().length > 0;
    this.terms$.next(value);
  }

  onKey(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.options.length) return;
      this.open = true;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.active = (this.active + step + this.options.length) % this.options.length;
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = this.options[this.active];
      if (option) this.go(option);
    } else if (event.key === 'Escape') {
      // Close the results but stay in the box (blurring dropped focus to the top of the page).
      this.close();
    }
  }

  go(option: IOption): void {
    void this.router.navigate(option.link, { queryParams: option.queryParams });
    this.text = '';
    this.input.nativeElement.value = '';
    this.terms$.next('');
    this.close();
    this.input.nativeElement.blur();
  }

  close(): void {
    this.open = false;
  }

  /** What the status line under the box says to screen readers: searching, how many results, or none. */
  get status(): string {
    if (!this.open || !this.text.trim()) return '';
    // Still typing: say nothing until the search for what's in the box starts.
    if (this.text.trim() !== this.searchedTerm) return '';
    if (this.searching) return 'Searching…';
    const n = this.options.length;
    if (n) return `${n} result${n === 1 ? '' : 's'}. Use the arrow keys to choose one.`;
    if (this.lookup.reservationOnly) return `No reservation r-${this.lookup.reservationId}.`;
    if (!this.lookup.userTerm && !this.lookup.reservationId) return 'Type at least two characters.';
    return `Nothing found for “${this.text.trim()}”.`;
  }

  optionId(i: number): string {
    return `admin-search-option-${i}`;
  }

  userName(user: IUser): string {
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'No name';
  }

  /** The reservation for a code or number; undefined when there's none or it's unreadable. */
  private findReservation(lookup: ILookup): Observable<IReservation | undefined> {
    if (!lookup.reservationId) return of(undefined);
    return this.reservations.fetch(lookup.reservationId).pipe(
      map((r) => (r?.id ? r : undefined)),
      catchError(() => of(undefined)),
    );
  }

  private findUsers(lookup: ILookup): Observable<IUser[]> {
    if (!lookup.userTerm) return of([]);
    return this.users.search(lookup.userTerm, 1, 6).pipe(
      map((page) => page.users ?? []),
      catchError(() => of([])),
    );
  }

  /** Their booked or requested stays that haven't ended; none when they can't be read. */
  private findUpcoming(user: IUser): Observable<UpcomingBooking[]> {
    if (!user.id) return of([]);
    return combineLatest([this.reservations.fetchForGuest(user.id), this.reservations.fetchForHost(user.id)]).pipe(
      map(([asGuest, asHost]) => upcomingBookings(asGuest ?? [], asHost ?? [])),
      catchError(() => of([])),
    );
  }

  private toOptions(
    reservation: IReservation | undefined,
    users: IUser[],
    upcoming: UpcomingBooking[] = [],
  ): IOption[] {
    const options: IOption[] = [];
    if (reservation) {
      options.push({ key: `r${reservation.id}`, link: ['/reservations', reservation.id as number], reservation });
    }
    for (const user of users) {
      options.push({ key: `u${user.id}`, link: ['/support', 'customers', user.id as number], user });
      if (users.length === 1)
        for (const u of upcoming)
          options.push({
            key: `b${u.reservation.id}`,
            link: ['/reservations', u.reservation.id as number],
            upcoming: u,
          });
    }
    if (this.lookup.userTerm && users.length) {
      const term = this.lookup.userTerm;
      options.push({ key: 'all', link: ['/users'], queryParams: { search: term }, allUsers: term });
    }
    return options;
  }
}
